import React, { Component } from "react";
import { inject, observer } from "mobx-react";
import axios from "axios";
import { Button, Icon, Message, Segment, Table } from "semantic-ui-react";
import BotInstance from "./BotInstance";
import {
  findUsedSuspiciousCcys,
  getBotConfig,
  getBotState,
  sumBotInstanceOpenProfit,
  sumBotInstanceMoneyUsedInOpenTrades,
} from "../utils/botUtils";
import { formatCrypto, formatPercent, formatPercent2 } from "../utils/numberFormat";
import { getAuthHttpHeaders } from "../auth";
import { instantFromApi } from "../utils/dateTime";
import { ChronoUnit, Instant } from "js-joda";
import PausedIcon from "./PausedIcon";
import HangingIcon from "./HangingIcon";
import ErrorIcon from "./ErrorIcon";
import SuspiciousCcysIcon from "./SuspiciousCcysIcon";
import CurrencyPair from "../components/CurrencyPair";
import { avatar } from "../utils/avatar";
import DurationProgressList from "../components/DurationProgressList";
import ActivityIcon from "../utils/activityIcon";

@inject("store")
@observer
class BotInstances extends Component {
  state = {
    autoRefresh: true,
    botInstances: null,
    suspiciousCurrencies: null,
    withDisabled: false,
    withOthers: false,
  };

  constructor() {
    super();
    this.load = this.load.bind(this);
  }

  componentDidMount() {
    this.load();
    this.refreshTimer = setInterval(() => {
      this.state.autoRefresh && this.load();
    }, 60 * 1000);
  }

  componentWillUnmount() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }
  }

  async load() {
    this.setState({ loading: true, error: false });

    if (this.botInstanceComponent) {
      this.botInstanceComponent.wrappedInstance.load();
    }

    try {
      let axiosRequest = axios.create({
        baseURL: "api",
        timeout: 30 * 1000,
        headers: {
          Accept: "application/json",
          ...getAuthHttpHeaders(),
        },
        params: {
          withDisabled: this.state.withDisabled,
          withOthers: this.state.withOthers,
        },
      });

      let response = await axiosRequest.get("botInstance/summaries");
      // console.log('botInstance/summaries response', response);
      this.setState({
        botInstances: response.data.botInstances,
        suspiciousCurrencies: response.data.suspiciousCurrencies,
      });

      if (this.state.selBotInstance) {
        for (const bi of response.data.botInstances) {
          if (bi.id === this.state.selBotInstance.id) {
            this.setState({ selBotInstance: bi });
            break;
          }
        }
      }
    } catch (error) {
      console.error(error);
      this.setState({ error, botInstances: undefined });
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { loading, error } = this.state;
    const {
      store: { mobile },
    } = this.props;
    let botInstancesRows;
    if (this.state.botInstances) {
      botInstancesRows = this.state.botInstances.map(bi => {
        const config = getBotConfig(bi);
        const state = getBotState(bi);

        const openTradesCount = state.tradeList ? state.tradeList.length : 0;

        const openProfit = sumBotInstanceOpenProfit(bi);
        const moneyUsedInOpenTrades = sumBotInstanceMoneyUsedInOpenTrades(bi);
        const predictedProfit = openProfit + bi.closedTradesProfitTotal;

        const closedProfitToInitBalance =
          bi.closedTradesProfitTotal && config.tradeTotal ? bi.closedTradesProfitTotal / config.tradeTotal : 0;
        const runDays =
          instantFromApi(bi.createdAt).until(instantFromApi(bi.finishedAt) || Instant.now(), ChronoUnit.SECONDS) /
          (60 * 60 * 24);
        const closedProfitToInitBalancePerDay =
          runDays && config ? Math.pow(1 + closedProfitToInitBalance, 1 / runDays) - 1 : 0;

        const active = this.state.selBotInstance && bi.id === this.state.selBotInstance.id;

        const onIcon = <Icon name={"toggle " + (bi.enabled ? "on" : "off")} />;
        const botAvatar = avatar(bi.id, 16, { verticalAlign: "middle" });

        const usedSuspiciousCcys = findUsedSuspiciousCcys(bi, this.state.suspiciousCurrencies);

        const promisingTradeCount =
          state && state.bestBuyOpportunityMap ? Object.keys(state.bestBuyOpportunityMap).length : 0;

        return (
          <Table.Row
            key={bi.id}
            onClick={() => this.setState({ selBotInstance: bi })}
            style={{ cursor: "pointer" }}
            active={active}
          >
            {!mobile && (
              <Table.Cell style={{ textAlign: "left" }}>
                {onIcon} {botAvatar} {bi.id.substring(0, 4)}
                <PausedIcon botInstance={bi} />
                <ErrorIcon botInstance={bi} />
                <HangingIcon botInstance={bi} />
                <SuspiciousCcysIcon usedSuspiciousCcys={usedSuspiciousCcys} />
              </Table.Cell>
            )}
            {!mobile && <Table.Cell style={{ textAlign: "left" }}>{bi.exchangeAccountName}</Table.Cell>}
            {mobile && (
              <Table.Cell style={{ textAlign: "left" }}>
                {onIcon} {botAvatar} {bi.id.substring(0, 4)}
                <PausedIcon botInstance={bi} />
                <ErrorIcon botInstance={bi} />
                <HangingIcon botInstance={bi} /> {bi.exchangeAccountName}
                <SuspiciousCcysIcon usedSuspiciousCcys={usedSuspiciousCcys} />
              </Table.Cell>
            )}

            <Table.Cell
              active={active}
              positive={openProfit > 0}
              negative={openProfit < 0}
              style={{ fontWeight: "bold" }}
            >
              {openProfit && moneyUsedInOpenTrades && formatPercent2(openProfit / moneyUsedInOpenTrades)}{" "}
              {openProfit && config.tradeTotal && "(" + formatPercent2(openProfit / config.tradeTotal) + ")"}
            </Table.Cell>

            <Table.Cell active={active} style={{ position: "relative" }}>
              {state.bearMarket && <Icon name="lightning" color="red" />}
              {!state.bearMarket && <ActivityIcon ballCount={promisingTradeCount} />}
              {openTradesCount}/{config.maxOpenTrades || 1} + {bi.closedTradesCount}
              {state.tradeList &&
                config.forceSellAfterMinutes && <DurationProgressList config={config} trades={state.tradeList} />}
            </Table.Cell>

            <Table.Cell
              active={active}
              positive={predictedProfit > 0}
              negative={predictedProfit < 0}
              style={{ fontWeight: "bold" }}
            >
              {predictedProfit && formatPercent2(predictedProfit / config.tradeTotal)}
            </Table.Cell>

            <Table.Cell
              active={active}
              positive={bi.closedTradesProfitTotal > 0}
              negative={bi.closedTradesProfitTotal < 0}
            >
              {formatPercent2(closedProfitToInitBalance)}
            </Table.Cell>

            <Table.Cell
              active={active}
              positive={closedProfitToInitBalancePerDay > 0}
              negative={closedProfitToInitBalancePerDay < 0}
            >
              {formatPercent2(closedProfitToInitBalancePerDay)} ({runDays.toFixed(runDays < 10 ? 1 : 0)}d)
            </Table.Cell>

            <Table.Cell>{formatCrypto(config.tradeTotal)}</Table.Cell>
            <Table.Cell>
              {!mobile && formatCrypto(state.balance)} ({state.balance &&
                formatPercent(state.balance / config.tradeTotal)})
            </Table.Cell>
            {!mobile && <Table.Cell>{formatCrypto(bi.closedTradesProfitTotal)}</Table.Cell>}

            <Table.Cell style={mobile && { fontSize: "10px" }}>{bi.type}</Table.Cell>
            <Table.Cell style={mobile && { fontSize: "10px" }}>
              <CurrencyPair exchangeType={bi.exchangeType} currencyPair={config.currencyPair} />
            </Table.Cell>
            {!mobile && <Table.Cell>{bi.config.leverage}</Table.Cell>}
            {/* {!mobile && <Table.Cell>{bi.config.volumeTreshold}</Table.Cell>} */}
            {!mobile && (
              <Table.Cell>
                {config.sticksCount} * {config.sticksPeriod}
              </Table.Cell>
            )}
            {/* <Table.Cell>{config.belowAvgBuySignal ? formatPercent2(config.belowAvgBuySignal / 100) : null}</Table.Cell>
            <Table.Cell>{config.minReturn ? formatPercent2(config.minReturn / 100) : null}</Table.Cell>
            <Table.Cell>{config.forceSellAfterMinutes}</Table.Cell> */}
          </Table.Row>
        );
      });
    }

    return (
      <div>
        {error && <Message negative>Error reading data: {error.message}</Message>}
        <div style={{ overflowY: "auto" }}>
          <Table unstackable style={{ whiteSpace: "nowrap", textAlign: "center" }}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Name</Table.HeaderCell>
                {!mobile && <Table.HeaderCell>Exchange Account</Table.HeaderCell>}
                <Table.HeaderCell>
                  Open<br />Profit<br />% of Used Money In Trade<br />(% of init)
                </Table.HeaderCell>
                <Table.HeaderCell>
                  Open/Max<br />+ Closed<br />Trades
                </Table.HeaderCell>
                <Table.HeaderCell>
                  Open+Closed<br />Profit<br />% of init
                </Table.HeaderCell>
                <Table.HeaderCell>
                  Closed<br />Profit<br />% of init
                </Table.HeaderCell>
                <Table.HeaderCell>
                  Closed<sup>^1</sup>/<sub>days</sub>
                </Table.HeaderCell>
                <Table.HeaderCell>Trade Total (init)</Table.HeaderCell>
                <Table.HeaderCell>Balance (% of init)</Table.HeaderCell>
                {!mobile && (
                  <Table.HeaderCell>
                    Closed<br />Profit
                  </Table.HeaderCell>
                )}
                <Table.HeaderCell>Type</Table.HeaderCell>
                <Table.HeaderCell>CCY</Table.HeaderCell>
                {!mobile && <Table.HeaderCell>Leverage</Table.HeaderCell>}
                {/* {!mobile && (
                  <Table.HeaderCell>
                    Volume<br />Treshold
                  </Table.HeaderCell>
                )} */}
                {!mobile && <Table.HeaderCell>Sticks</Table.HeaderCell>}
                {/* <Table.HeaderCell>
                  <small>
                    Buy<br />Signal
                  </small>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <small>
                    Min<br />Return
                  </small>
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <small>
                    Sell<br />After<br />min.
                  </small>
                </Table.HeaderCell> */}
              </Table.Row>
            </Table.Header>
            <Table.Body>{botInstancesRows}</Table.Body>
          </Table>
        </div>
        <Button.Group>
          <Button content="Refresh" icon="refresh" onClick={this.load} loading={loading} />
          <Button
            content="(auto)"
            toggle
            active={this.state.autoRefresh}
            onClick={() =>
              this.setState({
                autoRefresh: !this.state.autoRefresh,
              })
            }
          />
        </Button.Group>
        &nbsp;
        <Button
          content="Disabled"
          icon="snowflake outline"
          toggle
          active={this.state.withDisabled}
          onClick={() => this.setState({ withDisabled: !this.state.withDisabled }, () => this.load())}
        />
        &nbsp;
        <Button
          content="Other's"
          icon="asterisk"
          toggle
          active={this.state.withOthers}
          onClick={() => this.setState({ withOthers: !this.state.withOthers }, () => this.load())}
        />
        {this.state.selBotInstance && (
          <Segment>
            <BotInstance
              ref={i => (this.botInstanceComponent = i)}
              botInstance={this.state.selBotInstance}
              usedSuspiciousCcys={findUsedSuspiciousCcys(this.state.selBotInstance, this.state.suspiciousCurrencies)}
              onRefresh={this.load}
            />
          </Segment>
        )}
      </div>
    );
  }
}

export default BotInstances;



// WEBPACK FOOTER //
// ./src/bot/BotInstances.js